/*
 * Confidential and Proprietary.
 * Do not distribute without Bloom Harbor Gifts consent.
 * Copyright Bloom Harbor Gifts 2019. All rights reserved.
 */

import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import {
    object,
    arrayOf,
    array,
    func,
    string,
    shape,
    bool,
    number,
} from 'prop-types';
import Grid from '@material-ui/core/Grid';
import { bindActionCreators } from 'redux';
import { getBrand } from '../../../../../../../../state/ducks/App/ducks/Brand/Brand-Selectors';
import AddonDescBuilder from './AddonItemsDescBuilder';
import { addToCartDataType, addonGroupsType } from '../../../common/types';
import { trackEvent } from '../../../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import { buildComponentList } from '../../../../../../../helpers/tracking/utils/trackingUtils';
import {
    generateDataLayerProductArrays, convertToProduct,
} from '../../../../../../../helpers/tracking/vendors/Tealium/tealiumProductDataLayerHelpers';

const AddonItemsContainer = ({
    brand,
    product,
    product: {
        partNumber,
        brandId,
    },
    skuData,
    style,
    onCloseModal,
    categoryId,
    subscription,
    subscriptionDetails,
    presentation_family,
    disableAddToCart,
    isAddToCartEnabled,
    categoryPath,
    categoryName,
    selectedSku,
    updateWineStatus,
    productSkus,
    deliveryData,
    isMobile,
    handleMiniCartModalClick,
    mobileAgeVerificationOpen,
    handleMobileAgeVerification,
    addToCartData,
    addonGroups,
    track,
    handleAddonChange,
    handleAddonAdd,
    mixMatch,
    selectedAddons,
    selectedMixMatchComponent,
}) => {
    const [addons] = addonGroups;
    const maxAllowedAddons = addons?.maxAllowedAddons;

    useEffect(() => {
        const allAddonsItems = addonGroups?.map((group) => group.addons) || [];
        let listComponents = null;
        if (allAddonsItems.length > 0) {
            listComponents = [].concat([], ...allAddonsItems); // concatenating multiple groups to single
        }
        if (listComponents) {
            const addonTrackList = generateDataLayerProductArrays({
                products: convertToProduct(buildComponentList(listComponents, 'AddOn Products', 'Category') || {}),
                impressions: true,
                props: {},
            });
            track({
                eventCategory: 'Product Details',
                eventAction: 'Addons Displayed',
                ...addonTrackList,
            });
        }
    }, []);

    return (
        <Grid container>
            <Grid item sm={12} className={isMobile ? style.addonDialog : style.wine}>
                <AddonDescBuilder
                    handleMiniCartModalClick={handleMiniCartModalClick}
                    brand={brand}
                    maxAllowedAddons={maxAllowedAddons}
                    product={product}
                    addonsData={addonGroups}
                    skuData={skuData}
                    onCloseModal={onCloseModal}
                    brandId={brandId}
                    partNumber={partNumber}
                    categoryId={categoryId}
                    subscription={subscription}
                    subscriptionDetails={subscriptionDetails}
                    presentation_family={presentation_family}
                    disableAddToCart={disableAddToCart}
                    isAddToCartEnabled={isAddToCartEnabled}
                    categoryPath={categoryPath}
                    categoryName={categoryName}
                    selectedSku={selectedSku}
                    productSkus={productSkus}
                    updateWineStatus={updateWineStatus}
                    deliveryData={deliveryData}
                    isMobile={isMobile}
                    mobileAgeVerificationOpen={mobileAgeVerificationOpen}
                    handleMobileAgeVerification={handleMobileAgeVerification}
                    addToCartData={addToCartData}
                    track={track}
                    handleAddonChange={handleAddonChange}
                    handleAddonAdd={handleAddonAdd}
                    mixMatch={mixMatch}
                    selectedAddons={selectedAddons}
                    selectedMixMatchComponent={selectedMixMatchComponent}
                />
            </Grid>
        </Grid>
    );
};

AddonItemsContainer.propTypes = {
    brand: shape({
        domain: string,
    }).isRequired,
    product: shape({
        id: string.isRequired,
        name: string.isRequired,
        brandId: string.isRequired,
        partNumber: string.isRequired,
    }).isRequired,
    skuData: arrayOf(
        shape({
            id: string.isRequired,
            name: string.isRequired,
            prices: arrayOf(
                shape({
                    type: string.isRequired,
                    value: number.isRequired,
                }),
            ).isRequired,
        }),
    ),
    onCloseModal: func.isRequired,
    style: object, // Classes object
    categoryId: string.isRequired,
    subscriptionDetails: shape({
        subscriptionType: string,
        subscriptionDuration: string,
        subscriptionFrequency: string,
    }),
    subscription: shape({
        subscriptionKey: array,
        subscriptionIndefinite: string,
        subscriptionType: array,
        implementProductSubscription: bool,
        viewSubscription: bool,
        isTypeDiscovery: bool,
    }),
    presentation_family: string.isRequired,
    disableAddToCart: bool,
    isAddToCartEnabled: bool,
    categoryPath: string.isRequired,
    categoryName: string.isRequired,
    selectedSku: shape({
        id: string.isRequired,
        minAge: string.isRequired,
    }).isRequired,
    productSkus: shape({
        minAge: string.isRequired,
    }).isRequired,
    deliveryData: shape({
        location_type_options: arrayOf(
            shape({
                name: string,
                value: string,
            }),
        ),
        show: bool,
        title: string,
        zip_code_title: string,
        location_type_title: string,
    }).isRequired,
    updateWineStatus: func.isRequired,
    handleMiniCartModalClick: func.isRequired,
    isMobile: bool,
    mobileAgeVerificationOpen: bool,
    handleMobileAgeVerification: func,
    addToCartData: addToCartDataType,
    addonGroups: addonGroupsType.isRequired,
    track: func,
    handleAddonChange: func,
    handleAddonAdd: func,
    mixMatch: bool,
    selectedAddons: arrayOf(shape({
        id: string,
        ageVerifyFlag: bool,
        partNumber: string,
        name: string,
    })),
    selectedMixMatchComponent: arrayOf(shape({
        brandCode: string,
        componentType: string,
        productId: string,
        productCode: string,
    })),
};

AddonItemsContainer.defaultProps = {
    style: {},
    skuData: [],
    isAddToCartEnabled: true,
    subscriptionDetails: {},
    disableAddToCart: false,
    subscription: {},
    isMobile: false,
    mobileAgeVerificationOpen: false,
    handleMobileAgeVerification: () => { },
    addToCartData: {},
    track: () => {},
    handleAddonChange: () => {},
    handleAddonAdd: () => {},
    mixMatch: false,
    selectedAddons: [],
    selectedMixMatchComponent: [],
};
const mapStateToProps = (state) => ({
    brand: getBrand(state),
});

const mapDispatchToProps = (dispatch) => ({
    track: bindActionCreators(trackEvent, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(AddonItemsContainer);
